// Shared JSend handling for the tenant GPU server hooks (docs/api-types/gpu-servers.md).
//
// - Controllers answer `success` or `error` at HTTP 200; show an `error`'s `message` verbatim.
// - The ROUTER answers `fail` (`{ data: { msg } }`, no `message`) for a route that doesn't exist —
//   before the backend is deployed, or for a non-numeric id. Never render `data.msg`.
// - A non-JSON body is an error too. Never branch on `response.ok`.

/** Shown for the router's `fail` (e.g. the frontend deployed before the backend). */
export const GPU_SERVERS_UNAVAILABLE_MESSAGE = 'GPU servers are not available yet.';

/** The fields every GPU JSend body may carry, whatever its payload. */
export interface GpuJsendEnvelope {
  status?: unknown;
  message?: unknown;
  data?: unknown;
}

/** The parsed JSON body, or `null` when it isn't JSON (or isn't an object). */
export async function readJsendBody<T extends GpuJsendEnvelope>(response: Response): Promise<T | null> {
  const body: unknown = await response.json().catch(() => null);
  return typeof body === 'object' && body !== null ? (body as T) : null;
}

/**
 * The message to show for a non-success body: an `error`'s `message`, the "not available yet" text
 * for the router's `fail`, else `fallback` (non-JSON body, or a success missing its payload).
 */
export function jsendErrorMessage(body: GpuJsendEnvelope | null, fallback: string): string {
  if (body !== null && typeof body.message === 'string' && body.message !== '') {
    return body.message;
  }
  if (body?.status === 'fail') {
    return GPU_SERVERS_UNAVAILABLE_MESSAGE;
  }
  return fallback;
}

/** The per-field messages of a JSend `error` (`data.<field>`: string); `{}` otherwise. */
export function jsendFieldErrors(body: GpuJsendEnvelope | null): Record<string, string> {
  if (body?.status !== 'error' || typeof body.data !== 'object' || body.data === null) {
    return {};
  }
  const fieldErrors: Record<string, string> = {};
  for (const [field, value] of Object.entries(body.data as Record<string, unknown>)) {
    if (typeof value === 'string') {
      fieldErrors[field] = value;
    }
  }
  return fieldErrors;
}

/** `true` for the rejection of a fetch that we aborted ourselves. */
export function isAbortError(err: unknown, signal: AbortSignal): boolean {
  return signal.aborted || (err instanceof DOMException && err.name === 'AbortError');
}
